import React, { ComponentType, ErrorInfo, FC } from 'react';
import {
  reportLog
} from '@shared/analytics';
import {
  selectSessionKey,
  selectShortProfile,
  useSessionSelector
} from '@entities/session';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type State = { hasError: boolean };

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    return this.props.children;
  }
}

export const withErrorBoundary = <P extends object>(
  WrappedComponent: ComponentType<P>
) => {
  return function WrappedWithErrorBoundary(props: P) {
    const sessionKey = useSessionSelector(selectSessionKey);
    const shortProfile = useSessionSelector(selectShortProfile);

    const handleError = async (error: Error, errorInfo: ErrorInfo): Promise<void> => {
      await reportLog(sessionKey, {
        kind: 'error',
        type: 'unhandled-error',
        user: { partyId: shortProfile?.partyId },
        payload: {
          stack: error.stack,
          message: error.message,
          componentStack: errorInfo.componentStack,
        },
      });
    };

    return (
      <ErrorBoundary onError={handleError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
